import { Options, Vue } from 'vue-class-component';
import { mapState } from 'vuex';
import UsabillaInPageComponent from '@/sharedcomponents/pageComponents/usabillaInPageComponent/UsabillaInPageComponent.vue';
import { FamilySettings } from './FamilySettings';
import { FamilyModel, STEPS } from './FamilyModel';
import FamilyCalculator from './FamilyCalculator';
import ChoosePackageStepComponent from '../commonSteps/ChoosePackageStepComponent.vue';
import ContactInformationStepComponent from '../commonSteps/ContactInformationStepComponent.vue';
import OverviewStepComponent from '../commonSteps/OverviewStepComponent.vue';
import AdditionalInfoStepFamilyChildComponent from '../commonSteps/AdditionalInfoStepFamilyChildComponent.vue';
import PersonInfoStepComponent from './steps/PersonInfoStepComponent.vue';
import FamilyInfoStepComponent from './steps/FamilyInfoStepComponent.vue';
import PaymentStepComponent from '../commonSteps/PaymentStepComponent.vue';
import ReceiptStepComponent from '../commonSteps/ReceiptStepComponent.vue';
import CampaignStepComponent from '../commonSteps/CampaignStepComponent.vue';
import { PropType } from 'vue';
import { CardBlock, HtmlBlock, UsabillaInPageBlock } from '@/cms/definitions/content-types';
import store from '@/store/store';
import { CALCULATOR_LOADED, INIT_CALCULATOR, RESET_CALCULATORS } from '@/store/modules/calculatorContext';
import { gotoAddInsurances, nextStep, setFirstCard } from '../services/NavigationService';
import { checkForValidAddress, resetSelectedCalculation, subscribeBasketChange } from '../services/CalculationService';
import {
	getShowReceipt,
	handleValidCustomerAge,
	isValidAdditionalInfo,
	isValidAlmbrandProducts,
	isValidChildrenCpr,
	isValidContactInfo,
	isValidPackage,
	isValidPaymentInfo,
	regExCleanInput,
	showValidNowOption,
	Validator,
} from '../services/ValidationService';
import {
	getCardTitle,
	getSubtitle,
	setDetailsYearlyPrice,
	setGlobalPackageSums,
	setHighLights,
} from '../services/ProductService';
import { initComponent, STEP } from '../services/UiStepService';
import MapOrderDescription from './MapOrderDescription';
import { CalculatorUiComponent } from '../BuyInsuranceComponent';

@Options({
	name: 'BuyInsuranceFamilyComponent',
	props: {
		calculatorInfoBlock: Object as PropType<Array<HtmlBlock>>,
		contentBlocks: Object as PropType<Array<CardBlock>>,
		cardReceipt: Object as PropType<CardBlock>,
		settingsBlock: Object as PropType<HtmlBlock>,
		usabillaBlock: Object as PropType<UsabillaInPageBlock>,
		contentUrl: String, // Used by service to handle browser history
		headerCards: Object as PropType<Map<string, string>>,
	},
	components: {
		UsabillaInPageComponent,
		PersonInfoStepComponent,
		FamilyInfoStepComponent,
		ChoosePackageStepComponent,
		OverviewStepComponent,
		AdditionalInfoStepFamilyChildComponent,
		ContactInformationStepComponent,
		PaymentStepComponent,
		ReceiptStepComponent,
		CampaignStepComponent,
	},
	computed: mapState<any>({
		model: (state) => state.calculatorContext[state.calculatorContext.active].model,
		cms: (state) => state.calculatorContext[state.calculatorContext.active].cms,
	}),
})
export default class BuyFamily extends Vue implements CalculatorUiComponent {
	contentBlocks: Array<CardBlock>;
	cardReceipt: CardBlock;
	settingsBlock: HtmlBlock;
	usabillaBlock: UsabillaInPageBlock;
	contentUrl: string; // Used by service to handle browser history
	headerCards: Map<string, string>;
	calculatorInfoBlock: HtmlBlock;

	// list and order of steps
	public steps: Array<string> = STEPS;

	public model!: FamilyModel; // model from store
	public cms!: FamilySettings; // setting from store
	public calculator: FamilyCalculator = null;
	public componentInit = false;
	public checkout = false;
	private mapOrderDescription: MapOrderDescription;
	private unsubScribeBasketChange: Function;

	public created() {
		this.initCreated();
	}

	private async initCreated() {
		if (store.getters.getActiveCalculator) {
			await store.dispatch(CALCULATOR_LOADED);
		} else {
			// this is first load - update store
			const cms = new FamilySettings(this.settingsBlock);
			const model: FamilyModel = new FamilyModel();
			if (cms.mockData) {
				FamilyModel.mock(model);
			}
			await store.dispatch(INIT_CALCULATOR, {
				cms,
				model,
			});
		}

		if (!(await initComponent(this))) {
			return;
		}

		this.calculator = new FamilyCalculator(this);

		setFirstCard(this);
	}
	public mounted(): void {
		this.unsubScribeBasketChange = subscribeBasketChange(this);
	}

	beforeUnmount() {
		if (this.unsubScribeBasketChange) {
			this.unsubScribeBasketChange();
		}
	}

	// called by service
	public addResetSubscription() {
		const unsubscribe = store.subscribeAction((action, state) => {
			if (action.type === RESET_CALCULATORS) {
				setTimeout(() => {
					this.checkout = true;
					store.dispatch(INIT_CALCULATOR, {
						cms: this.cms,
						model: new FamilyModel(),
					});
					unsubscribe();
				}, 2000);
			}
		});
	}

	public get showValidNowOption() {
		return showValidNowOption(this.model, this.cms);
	}

	public async gotoCard(cardName: string): Promise<boolean> {
		if (cardName === STEP.PERSON_INFO || cardName === STEP.PACKAGE) {
			resetSelectedCalculation(this.model);
		}

		if (cardName === STEP.CHILDREN_INFO) {
			// check address
			const ok = await checkForValidAddress(this);
			if (!ok) {
				this.model.currentCardName = cardName;
				this.gotoCard(STEP.PERSON_INFO);
				return false;
			}
		}

		if (cardName === STEP.PACKAGE) {
			this.calculator.setupExcessList();
			// check Age:
			if (!handleValidCustomerAge(this.model, this.cms)) {
				this.gotoCard(STEP.PERSON_INFO);
				return false;
			}
		}

		if (cardName === STEP.PAYMENT) {
			// force user to choose yearly/monthly payment to ensure progressive steps
			this.model.choosePackage.monthYear = undefined;
			this.model.payment.userSelected = false;
		}

		this.model.currentCardName = cardName;

		if (cardName === STEP.ORDER) {
			store.state.showSpinner = true;
			if (!this.isValid(this.model.currentCardName)) {
				store.state.showSpinner = false;
				return false;
			}
			this.mapOrderDescription = new MapOrderDescription(this);

			await this.mapOrderDescription.orderByEmail();
			return false;
		}
		return true;
	}

	/**
	 * when nextbtn is clicked - check for valid
	 * @param cardName
	 */
	public nextStep(cardName: string, addToHistory = true) {
		nextStep(cardName, addToHistory, this);
	}

	public getSubtitle(cardName: string) {
		return getSubtitle(cardName, this.model);
	}

	public isActiveCard(cardName: string): boolean {
		return cardName === this.model.currentCardName;
	}

	/* eslint-disable */
	public isValid(cardName): boolean {
		switch (cardName) {
			case STEP.PERSON_INFO:
				const okPerson =
					this.model.personInfo.address &&
					this.model.personInfo.familyMemberCount &&
					Validator.isValidAge(this.model.personInfo.customerAge) &&
					this.model.personInfo.thatchedRoof &&
					isValidAlmbrandProducts(this.model);
				if (okPerson) {
					this.model.personInfo.customerAge = this.model.personInfo.customerAge.trim().replace(/\D/g, '');
					this.model.personInfo.subtitle = `${this.model.personInfo.customerAge} år, ${this.model.personInfo.address}`;
					this.model.personInfo.subtitle = regExCleanInput(this.model.personInfo.subtitle);
				}
				return okPerson;
			case STEP.CHILDREN_INFO:
				const ok = this.isPersonsFilled();
				if (ok) {
					if (this.model.familyInfo.personCountId === 0 && this.model.familyInfo.age0 !== undefined) {
						this.model.familyInfo.subtitle =
							this.model.familyInfo.name0 + ' ' + this.model.familyInfo.age0 + ' år';
					} else if (this.model.familyInfo.personCountId > 0 && this.model.familyInfo.personCount) {
						this.model.familyInfo.subtitle = this.model.familyInfo.personCount;
					} else {
						this.model.familyInfo.subtitle = '';
					}
					this.model.familyInfo.subtitle = regExCleanInput(this.model.familyInfo.subtitle);
				}
				if (!ok) {
					this.model.familyInfo.subtitle = '';
				}
				return ok;
			case STEP.PACKAGE:
				return isValidPackage(this) && this.model.choosePackage.insuredValueAmount !== -1;
			case STEP.OVERVIEW:
				return isValidPackage(this);
			case STEP.CONTACT_INFORMATION:
				if (!isValidContactInfo(this.model, this.cms)) {
					return false;
				}
				return isValidChildrenCpr(this.model);
			case STEP.ADDITIONAL_INFO:
				return isValidAdditionalInfo(false, this.model, this.cms);
			case STEP.PAYMENT:
				return isValidPaymentInfo(this.model, this.cms);
			default:
				return true;
		}
	}
	/* eslint-enable */

	private isPersonsFilled(): boolean {
		let ok = this.model.familyInfo.personCount !== 0;
		if (ok) {
			if (this.model.familyInfo.personCountId + 1 >= this.model.personInfo.familyMemberCountId) {
				ok = false;
			}
		}
		if (ok) {
			for (let i = 0; i <= this.model.familyInfo.personCountId; i++) {
				const age = 'age' + i;
				ok = this.model.familyInfo[age] !== undefined;
				if (!ok) {
					break;
				}
				if (this.cms.useAdultAccident) {
					const personAge = this.model.familyInfo[age];
					if (personAge > -1 && personAge < 100) {
						if (personAge >= 18) {
							if (!this.model.familyInfo['workId' + i]) {
								ok = false;
								break;
							}
						}
					}
				} else {
					if (this.cms.showWorkForMinors && this.model.familyInfo[age] >= 15) {
						if (!this.model.familyInfo['hasWork' + i]) {
							ok = false;
							break;
						}
						if (this.model.familyInfo['hasWork' + i] === 'ja') {
							if (!this.model.familyInfo['workId' + i]) {
								ok = false;
								break;
							}
						}
					}
				}
			}
		}
		return ok;
	}
	// called by service
	public setOverviewData() {
		setHighLights(this.model);

		this.model.overview.details = [
			this.model.personInfo.customerAge + ' år',
			this.model.personInfo.address,
			'Stråtag: ' + this.model.personInfo.thatchedRoof,
			'Husstand: ' + this.model.personInfo.familyMemberCount,
			this.model.familyInfo.personCount,
			'Selvrisiko: ' + this.getExcessAmount(),
			'Indbo sum: ' + this.model.choosePackage.insuredValue,
		];
		setGlobalPackageSums(this.model);
		setDetailsYearlyPrice(this.model, this.cms);
	}

	/** called by service */
	public buildDescriptionForEmail(desc: string) {
		return this.mapOrderDescription.buildDescriptionForEmail(desc);
	}

	public gotoAddInsurances(step: string) {
		gotoAddInsurances(step, this);
	}

	public get showReceipt() {
		return getShowReceipt(this);
	}

	public getCardTitle(card): string {
		return getCardTitle(card, this.model, this.cms);
	}

	public getExcessAmount(): string {
		const inx = this.model.ownRiskOptions.indexOf(this.model.choosePackage.ownRiskId);
		return this.model.ownRiskLabels[inx];
	}
}
